/**
 * Shared queries used by the shell and many screens (sites, clients, routes, reason codes,
 * users, notifications). Query keys start with the tenant id so switching tenant never shows
 * another tenant's cached rows. The chosen site lives in ./site.tsx.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import { EP, type Res } from './endpoints';
import type { ReasonCategory } from './types';

export type PublicSettings = Res<'GET /settings/public'>;

export const usePublicSettings = () =>
  useQuery({ queryKey: ['settings', 'public'], queryFn: () => api<PublicSettings>(EP.settingsPublic, { anonymous: true }), staleTime: 10 * 60_000, retry: false });

/** Key prefix for anything tenant-scoped. */
export function useOrgKey(): string {
  const { user } = useAuth();
  return user?.org?.id ?? 'none';
}

const list = <T,>(r: T[] | { data: T[] }): T[] => (Array.isArray(r) ? r : r.data);

export function useSites(opts: { all?: boolean } = {}) {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({ queryKey: [org, 'sites', opts.all ? 'all' : 'active'], enabled: !!user?.org, queryFn: () => api<Res<'GET /sites'>>(EP.sites, { query: opts.all ? {} : { active: true } }).then(list), staleTime: 60_000 });
}

export function useClients() {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({ queryKey: [org, 'clients'], enabled: !!user?.org, queryFn: () => api<Res<'GET /clients'>>(EP.clients).then(list), staleTime: 60_000 });
}

export function useRoutes(siteId?: string) {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({ queryKey: [org, 'routes', siteId ?? 'all'], enabled: !!user?.org, queryFn: () => api<Res<'GET /routes'>>(EP.routes, { query: { siteId } }).then(list), staleTime: 60_000, retry: false }); // staff only (403 for portal users)
}

export function useReasonCodes(category?: ReasonCategory) {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({ queryKey: [org, 'reason-codes', category ?? 'all'], enabled: !!user?.org, queryFn: () => api<Res<'GET /reason-codes'>>(EP.reasonCodes, { query: { category, active: true } }).then(list), staleTime: 5 * 60_000 });
}

export function useOrgUsers(enabled = true) {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'users', 'refs'], enabled, queryFn: () => api<Res<'GET /users/directory'>>(EP.usersDirectory).then((rows) => rows.map((u) => ({ id: u.id, name: u.name }))), staleTime: 5 * 60_000, retry: false });
}

export function useNotifications() {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({
    queryKey: [org, 'notifications'],
    enabled: !!user,
    queryFn: () => api<Res<'GET /notifications'>>(EP.notifications, { query: { pageSize: 20 }, background: true }).then(list),
    refetchInterval: 60_000,
    retry: false,
  });
}
